import { useState } from "react";
import { projectFileName } from "../../storage";
import type { ReportModel } from "./model";
import { downloadBlob, downloadBytes, downloadText } from "../../lib/download";
import { buildDxf, encodeDxf } from "../../export/dxf";
import { fileDigest } from "./hash";
import { buildQuantitiesCsv } from "../../export/csv";
import { completePricing } from "../project/PricingDialog";
import type { DocxImages } from "../../export/docx";
import { svgToPng, dataUrlToBytes } from "../../export/svgToPng";

const base = (m: ReportModel): string => projectFileName(m.project).replace(/\.tasduvar\.json$/, "");
const slug = (s: string): string => s.toLocaleLowerCase("tr").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "duvar";

/** Ekrandaki rapor SVG'lerini (kesit / kuvvet şeması) PNG'ye çevirir; tarayıcı dışında boş döner. */
export async function collectImages(m: ReportModel): Promise<DocxImages> {
  const out: DocxImages = {};
  for (const { wall } of m.walls) {
    const root = document.querySelector(`[data-section="drawing"][data-wall="${CSS.escape(wall.name)}"] svg`);
    const forces = document.querySelector(`[data-section="forceDiagram"][data-wall="${CSS.escape(wall.name)}"] svg`);
    const entry: DocxImages[string] = {};
    try { if (root instanceof SVGSVGElement) entry.drawing = dataUrlToBytes((await svgToPng(root)).dataUrl); } catch { /* görsel yoksa metin notu basılır */ }
    try { if (forces instanceof SVGSVGElement) entry.forces = dataUrlToBytes((await svgToPng(forces)).dataUrl); } catch { /* aynı */ }
    out[wall.id] = entry;
  }
  return out;
}

/** Rapor paftası dışa aktarmaları (Word, DXF, XLSX, CSV) — şerit Rapor sekmesi `cadUi.exportRequest` ile tetikler. */
export function useReportExports(model: ReportModel | null, onLog: (f: "docx" | "dxf" | "xlsx" | "csv") => Promise<void>) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const dxf = (): void => {
    if (!model) return;
    const walls = model.walls.filter((w) => w.result);
    const digest = fileDigest(model.project);
    for (const { wall, result } of walls) downloadBytes(`${base(model)}-${slug(wall.name)}.dxf`, encodeDxf(buildDxf(wall, result!, { projectName: model.project.info.name, digest })), "application/dxf");
    void onLog("dxf");
  };
  const docx = async (): Promise<void> => {
    if (!model) return;
    setBusy("Word hazırlanıyor…"); setErr(null);
    try { const { buildDocx } = await import("../../export/docx");   // docx paketi yalnız gerektiğinde yüklenir
      const blob = await buildDocx(model, await collectImages(model)); downloadBlob(`${base(model)}.docx`, blob); await onLog("docx"); }
    catch (e) { setErr(e instanceof Error ? e.message : "Word üretilemedi"); }
    finally { setBusy(null); }
  };
  const qWalls = model ? model.walls.filter((w) => w.quantities).map((w) => ({ name: w.wall.name, q: w.quantities! })) : [];
  const csv = (): void => { if (!model || !qWalls.length) return; downloadText(`${base(model)}-metraj.csv`, buildQuantitiesCsv(qWalls, completePricing(model.project.pricing)), "text/csv;charset=utf-8"); void onLog("csv"); };
  const xlsx = async (): Promise<void> => {
    if (!model || !qWalls.length) return;
    setBusy("Excel hazırlanıyor…"); setErr(null);
    try { const { buildQuantitiesXlsx } = await import("../../export/xlsx"); downloadBlob(`${base(model)}-metraj.xlsx`, await buildQuantitiesXlsx(qWalls, completePricing(model.project.pricing), model.project.info.name)); await onLog("xlsx"); }
    catch (e) { setErr(e instanceof Error ? e.message : "Excel üretilemedi"); }
    finally { setBusy(null); }
  };
  return { busy, err, setErr, dxf, docx, csv, xlsx, hasQuantities: qWalls.length > 0, hasResult: !!model?.walls.some((w) => w.result) };
}
